import { MeshBuilder, Vector3, type Mesh } from "@babylonjs/core";
import { Btn, HeroId, Slot, fxk, hv, pk } from "@rivalwatch/shared";
import { baked } from "../../audio/bake.ts";
import { choir } from "../../audio/synth.ts";
import { bell, click, drive, fm, later, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import { glow } from "../../game/materials.ts";
import type { ClientHero } from "../types.ts";

const PK_RIFT = pk(HeroId.Tragik, 0);
const FX_RIFT = fxk(HeroId.Tragik, 0);
const hz = (semi: number) => 293.66 * 2 ** (semi / 12);

const theme = {
  bpm: 84,
  octave: 0,
  voice: choir,
  notes: [
    [0, 0.75],
    [4, 0.5],
    [6, 0.75],
    [11, 1],
  ],
} satisfies ClientHero["audio"]["theme"];

const swordCue = baked("fire:tragik:rune-sword", () =>
  trim(13, (o) => {
    click(o, 0.28, 4200, 0.004);
    whoosh(o, 0.42, 900, 3400, 0.16);
    fm(o, { f: hz(18), ratio: 2.2, index: 4, index1: 0.4, gain: 0.08, d: 0.22 });
    noise(drive(o, 1.7), { color: "crackle", type: "bandpass", f: 2600, q: 1.1, gain: 0.18, d: 0.12 });
    return 0.42;
  }), { variants: 5, jitter: 0.015 });

const riftCutCue = baked("ability:tragik:rift-cut", () =>
  trim(10.8, (o) => {
    tone(o, { type: "sawtooth", f: hz(-12), f1: hz(8), glide: 0.38, gain: 0.06, a: 0.08, d: 0.35, lp: 900 });
    whoosh(o, 0.55, 500, 3600, 0.28);
    bell(later(o, 0.18), hz(18), 0.35, 0.55);
    return 0.85;
  }), { variants: 3, jitter: 0.01 });

const understepCue = baked("ability:tragik:understep", () =>
  trim(2.6, (o) => {
    thump(o, 0.55, 95, 38, 0.22);
    noise(o, { color: "pink", type: "bandpass", f: 300, f1: 2200, q: 1.3, gain: 0.55, a: 0.02, d: 0.3 });
    fm(later(o, 0.06), { f: hz(6), ratio: 1.5, index: 5, index1: 0, gain: 0.12, d: 0.28 });
    return 0.62;
  }), { variants: 3, jitter: 0.01 });

const lungeCue = baked("ability:tragik:upcast", () =>
  trim(5.0, (o) => {
    whoosh(o, 0.65, 700, 4500, 0.22, 0, 1.3);
    thump(later(o, 0.12), 0.48, 130, 52, 0.16);
    fm(later(o, 0.1), { f: hz(16), ratio: 2.8, index: 4, index1: 0.2, gain: 0.1, d: 0.18 });
    return 0.55;
  }), { variants: 3, jitter: 0.01 });

const omenCue = baked("ability:tragik:omen", () =>
  trim(-5.5, (o) => {
    thump(o, 0.95, 70, 24, 0.65);
    tone(drive(o, 2), { type: "sawtooth", f: hz(-24), f1: hz(-7), glide: 0.75, gain: 0.13, a: 0.12, d: 0.6, lp: 1100 });
    bell(later(o, 0.22), hz(18), 0.6, 0.55);
    noise(o, { color: "crackle", type: "highpass", f: 2800, gain: 0.25, a: 0.05, d: 0.6 });
    return 1.35;
  }), { variants: 2, jitter: 0.005 });

type TragikGear = { blade: Mesh; edge: Mesh; guard: Mesh; disc: Mesh; halo: Mesh; hornL: Mesh; hornR: Mesh; hair: Mesh };

const hero: ClientHero<TragikGear> = {
  id: HeroId.Tragik,
  bulk: 0.95,
  shape: { head: 0.95, torso: 1.05, arms: 1.05, legs: 1.05 },
  gear(g) {
    const { scene, rig, add, addWorld, glow, toon, R, H } = g;
    const blade = add(MeshBuilder.CreateBox("sorrow-edge", { width: 0.12, height: 0.08, depth: H * 0.86 }, scene), rig.handR, glow("#ff7a3d"));
    blade.position.z = -H * 0.32;
    const edge = add(MeshBuilder.CreateBox("sorrow-edge-core", { width: 0.045, height: 0.12, depth: H * 0.92 }, scene), rig.handR, glow("#ffd36a"));
    edge.position.z = -H * 0.34;
    const guard = add(MeshBuilder.CreateBox("sword-guard", { width: 0.5, height: 0.08, depth: 0.08 }, scene), rig.handR, toon("#fff0b3"));
    guard.position.z = -0.08;
    const disc = add(MeshBuilder.CreateTorus("back-rift", { diameter: R * 1.8, thickness: 0.035, tessellation: 36 }, scene), rig.torso, glow("#f06a3b", 0.8));
    disc.rotation.x = Math.PI / 2;
    disc.position.z = R * 0.82;
    const halo = addWorld(MeshBuilder.CreateTorus("portal-shadow", { diameter: 1.4, thickness: 0.04, tessellation: 40 }, scene), glow("#3f2b66", 0.75));
    halo.rotation.x = Math.PI / 2;
    const hornL = add(MeshBuilder.CreateCylinder("dark-horn-l", { diameterTop: 0.02, diameterBottom: 0.09, height: 0.45, tessellation: 8 }, scene), rig.head, toon("#2a1438"));
    const hornR = add(MeshBuilder.CreateCylinder("dark-horn-r", { diameterTop: 0.02, diameterBottom: 0.09, height: 0.45, tessellation: 8 }, scene), rig.head, toon("#2a1438"));
    hornL.position.set(-R * 0.42, 0.32, 0.02);
    hornR.position.set(R * 0.42, 0.32, 0.02);
    hornL.rotation.z = -0.55;
    hornR.rotation.z = 0.55;
    const hair = add(MeshBuilder.CreateBox("blonde-warrior-hair", { width: R * 1.15, height: 0.16, depth: R * 0.8 }, scene), rig.head, toon("#f2d36b"));
    hair.position.set(0, 0.24, 0.03);
    return { blade, edge, guard, disc, halo, hornL, hornR, hair };
  },
  animate(gear, f, rig) {
    const dark = f.ultActive || f.v[2] > 0.01;
    gear.blade.scaling.set(dark ? 1.8 : 1, dark ? 1.4 : 1, dark ? 1.3 : 1);
    gear.edge.scaling.copyFrom(gear.blade.scaling);
    gear.blade.rotation.z = Math.sin(f.t * 8) * (dark ? 0.25 : 0.08);
    gear.edge.rotation.z = gear.blade.rotation.z;
    gear.disc.rotation.y = f.t * (dark ? 3 : 1.2);
    gear.disc.scaling.setAll(1 + (dark ? 0.35 : 0.08) * Math.sin(f.t * 5));
    gear.halo.position.set(f.pos.x, f.pos.y + 0.03, f.pos.z);
    gear.halo.scaling.setAll(f.v[0] > 0 ? 1.2 : 0.35);
    gear.halo.setEnabled(f.v[0] > 0 || f.moveKind === 1);
    gear.hornL.setEnabled(dark);
    gear.hornR.setEnabled(dark);
    gear.disc.setEnabled(true);
    for (const p of Object.values(rig.parts)) if (p) p.visibility = dark ? 0.82 : 1;
    if (dark) return f.action === 1 || f.action === 2 ? "attack-melee-right" : "holding-both";
    if (f.action === 1 || f.action === 2) return "attack-melee-right";
    return "holding-right";
  },
  hud(c, eid) {
    return [
      { label: "Portal follow-up", value: hv(c, eid, 0) / 3, color: "#f06a3b" },
      { label: "Rift charge", value: hv(c, eid, 1) / 2, color: "#fff0b3" },
    ];
  },
  projectiles: {
    [PK_RIFT]: (scene) => {
      const blade = MeshBuilder.CreateBox("rift-wave", { width: 0.08, height: 0.6, depth: 0.9 }, scene);
      blade.material = glow(scene, "#ff7a3d", 0.85);
      return {
        gear: blade,
        update(_m, fr) {
          blade.position.set(fr.pos.x, fr.pos.y, fr.pos.z);
          blade.rotation.y = Math.atan2(-fr.dir.x, -fr.dir.z);
        },
        dispose: () => blade.dispose(),
      };
    },
  },
  fx: {
    [FX_RIFT]: {
      explosion(c, pos, radius) {
        const ring = MeshBuilder.CreateTorus("tragik-burst", { diameter: radius * 2, thickness: 0.06, tessellation: 48 }, c.scene);
        ring.position.copyFrom(new Vector3(pos.x, pos.y + 0.08, pos.z));
        ring.rotation.x = Math.PI / 2;
        ring.material = c.glow("#ff7a3d", 0.7);
        c.timed(ring, 0.5, (t, m) => {
          m.scaling.setAll(0.4 + t * 0.8);
          m.visibility = 1 - t;
        });
      },
      keepGeneric: true,
    },
  },
  audio: {
    weight: "mid",
    fire: () => ({ key: "fire:tragik:rune-sword", cue: swordCue }),
    ability: {
      [Slot.Secondary]: riftCutCue,
      [Slot.Ability1]: understepCue,
      [Slot.Ability2]: lungeCue,
      [Slot.Ultimate]: omenCue,
    },
    theme,
    themeTrim: { heroic: 0.8, hostile: -5.5, tease: -1 },
  },
  icons: {
    [Slot.Primary]: "M5 17 C8 12 11 8 18 3 M14 4 L20 10 M4 18 L9 20",
    [Slot.Secondary]: "M4 12 C8 5 16 5 20 12 C16 19 8 19 4 12 Z M9 12 H15",
    [Slot.Ability1]: "M4 17 C8 21 16 21 20 17 M6 17 C9 12 15 12 18 17 M12 4 V14",
    [Slot.Ability2]: "M12 20 V4 M7 9 L12 4 L17 9 M6 16 C9 14 15 14 18 16",
    [Slot.Ultimate]: "M12 3 L15 9 L21 10 L16 14 L17 21 L12 17 L7 21 L8 14 L3 10 L9 9 Z",
    passive: "M5 15 C8 21 16 21 19 15 M8 13 C9 8 15 8 16 13",
  },
};

void Btn;
export default hero;
